// 家族のタスクを購読する hook
import { useCallback, useEffect, useState } from 'react';
import { AnyTask, Level } from '../types';
import {
  addTaskDb,
  deleteTaskDb,
  loadTasksByFamily,
  subscribeTaskChanges,
  updateTaskDb,
} from '../lib/db';
import { useAuth } from '../contexts/AuthContext';

export function useTasks(level?: Level) {
  const { user, family } = useAuth();
  const [tasks, setTasks] = useState<AnyTask[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!family) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const all = await loadTasksByFamily(family.id);
    setTasks(level ? all.filter((t) => t.level === level) : all);
    setLoading(false);
  }, [family, level]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Realtime購読
  useEffect(() => {
    if (!family) return;
    const unsub = subscribeTaskChanges(family.id, () => {
      reload();
    });
    return unsub;
  }, [family, reload]);

  const add = useCallback(
    async (task: Omit<AnyTask, 'id' | 'createdAt'>) => {
      if (!family || !user) return null;
      const created = await addTaskDb(task, family.id, user.id);
      await reload();
      return created;
    },
    [family, user, reload]
  );

  const update = useCallback(
    async (taskId: string, patch: Partial<AnyTask>) => {
      const ok = await updateTaskDb(taskId, patch);
      if (ok) await reload();
      return ok;
    },
    [reload]
  );

  const remove = useCallback(
    async (taskId: string) => {
      const ok = await deleteTaskDb(taskId);
      if (ok) await reload();
      return ok;
    },
    [reload]
  );

  return { tasks, loading, reload, add, update, remove };
}
